import React, { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { Globe, Shield, Eye, EyeOff, Save } from 'lucide-react';
import { db } from '../firebase';
import { useDemoMode } from '../context/DemoModeContext';

function sortByName(a, b) {
  const an = (a.farmName || a.name || a.id || '').toString();
  const bn = (b.farmName || b.name || b.id || '').toString();
  return an.localeCompare(bn);
}

export default function AdminSettings({ embedded = false }) {
  const {
    loading,
    isAdmin,
    settings,
    isDemoModeEnabled,
    getFarmDisplayName,
    getParcelDisplayName,
    setDemoEnabled,
    setIncludedFarmIds,
    setFarmAlias,
    setParcelAlias,
    // Phase 1: customer-facing public demo controls
    publicDemoFarms,
    publicDemoFarmIds,
    setFarmIsPublicDemo,
    setPublicDemoFarmAlias,
    setPublicDemoParcelAlias,
  } = useDemoMode();

  const [farms, setFarms] = useState([]);
  const [activeFarmId, setActiveFarmId] = useState('');
  const [parcels, setParcels] = useState([]);
  const [savingMessage, setSavingMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [farmAliasDrafts, setFarmAliasDrafts] = useState({});
  const [parcelAliasDrafts, setParcelAliasDrafts] = useState({});
  const [publicDemoAliasDrafts, setPublicDemoAliasDrafts] = useState({});
  // State for the public-demo parcel-aliases section. Independent from the
  // per-admin parcel section above: different farm filter (only public-demo
  // farms), different write target (farms/{id}.publicDemoParcelAliases),
  // visible to ALL signed-in users.
  const [activePublicDemoFarmId, setActivePublicDemoFarmId] = useState('');
  const [publicDemoParcels, setPublicDemoParcels] = useState([]);
  const [publicDemoParcelAliasDrafts, setPublicDemoParcelAliasDrafts] = useState({});

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'farms'),
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort(sortByName);
        setFarms(rows);
      },
      (error) => {
        console.error('AdminSettings farms listener failed:', error);
        setErrorMessage('Failed to load properties.');
      }
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!activeFarmId) {
      setParcels([]);
      return;
    }
    const unsub = onSnapshot(
      collection(db, 'farms', activeFarmId, 'parcels'),
      (snap) => {
        const rows = snap.docs.map((d) => {
          const data = d.data() || {};
          const key = data.analyticsParcelName || data.parcel_id || d.id;
          const fallback = data.customName || data.displayName || data.name || data.farm_field_name || key;
          return { docId: d.id, key: String(key), fallback };
        });
        rows.sort((a, b) => a.fallback.localeCompare(b.fallback));
        setParcels(rows);
      },
      (error) => {
        console.error('AdminSettings parcels listener failed:', error);
        setErrorMessage('Failed to load parcel aliases.');
      }
    );
    return () => unsub();
  }, [activeFarmId]);

  useEffect(() => {
    const nextFarmDrafts = {};
    for (const farm of farms) {
      nextFarmDrafts[farm.id] = settings.farmAliases?.[farm.id] || '';
    }
    setFarmAliasDrafts(nextFarmDrafts);
  }, [farms, settings.farmAliases]);

  // Seed drafts for the public-demo alias field from the live farm docs.
  useEffect(() => {
    const next = {};
    for (const farm of farms) {
      next[farm.id] = farm.publicDemoFarmAlias || '';
    }
    setPublicDemoAliasDrafts(next);
  }, [farms]);

  useEffect(() => {
    if (!activeFarmId) {
      setParcelAliasDrafts({});
      return;
    }
    const nextParcelDrafts = {};
    for (const parcel of parcels) {
      nextParcelDrafts[parcel.key] = settings.parcelAliases?.[activeFarmId]?.[parcel.key] || '';
    }
    setParcelAliasDrafts(nextParcelDrafts);
  }, [activeFarmId, parcels, settings.parcelAliases]);

  // Load parcels for the public-demo aliases section. Same key derivation as
  // the per-admin loader above so both alias maps key on the SAME parcel
  // identifier — important because getParcelDisplayName tries the same
  // candidate keys regardless of which alias map it ends up reading from.
  useEffect(() => {
    if (!activePublicDemoFarmId) {
      setPublicDemoParcels([]);
      return;
    }
    const unsub = onSnapshot(
      collection(db, 'farms', activePublicDemoFarmId, 'parcels'),
      (snap) => {
        const rows = snap.docs.map((d) => {
          const data = d.data() || {};
          const key = data.analyticsParcelName || data.parcel_id || d.id;
          const fallback = data.customName || data.displayName || data.name || data.farm_field_name || key;
          return { docId: d.id, key: String(key), fallback };
        });
        rows.sort((a, b) => a.fallback.localeCompare(b.fallback));
        setPublicDemoParcels(rows);
      },
      (error) => {
        console.error('AdminSettings public-demo parcels listener failed:', error);
        setErrorMessage('Failed to load public-demo parcel aliases.');
      }
    );
    return () => unsub();
  }, [activePublicDemoFarmId]);

  // Seed the public-demo parcel alias drafts from the live farm doc whenever
  // the active farm or the upstream publicDemoFarms array changes. Drafts let
  // the user edit without flicker and we save on blur.
  useEffect(() => {
    if (!activePublicDemoFarmId) {
      setPublicDemoParcelAliasDrafts({});
      return;
    }
    const farm = (publicDemoFarms || []).find((f) => f.id === activePublicDemoFarmId);
    const aliasMap = (farm && farm.publicDemoParcelAliases && typeof farm.publicDemoParcelAliases === 'object')
      ? farm.publicDemoParcelAliases
      : {};
    const next = {};
    for (const parcel of publicDemoParcels) {
      next[parcel.key] = aliasMap[parcel.key] || '';
    }
    setPublicDemoParcelAliasDrafts(next);
  }, [activePublicDemoFarmId, publicDemoParcels, publicDemoFarms]);

  const includedFarmIds = useMemo(
    () => (Array.isArray(settings.includedFarmIds) ? settings.includedFarmIds : []),
    [settings.includedFarmIds]
  );
  const includedSet = useMemo(() => new Set(includedFarmIds), [includedFarmIds]);
  const demoFarms = useMemo(() => farms.filter((farm) => includedSet.has(farm.id)), [farms, includedSet]);

  const handleToggleFarm = async (farmId, include) => {
    setErrorMessage('');
    setSavingMessage('');
    const next = include
      ? Array.from(new Set([...includedFarmIds, farmId]))
      : includedFarmIds.filter((id) => id !== farmId);
    try {
      await setIncludedFarmIds(next);
      setSavingMessage('Saved demo property scope.');
      if (!include && activeFarmId === farmId) setActiveFarmId('');
    } catch (error) {
      console.error('Failed to save demo scope:', error);
      setErrorMessage('Could not save demo property selection.');
    }
  };

  const handleDemoToggle = async () => {
    setErrorMessage('');
    setSavingMessage('');
    try {
      await setDemoEnabled(!isDemoModeEnabled);
      setSavingMessage(`Demo mode ${!isDemoModeEnabled ? 'enabled' : 'disabled'} for your admin session.`);
    } catch (error) {
      console.error('Failed to toggle demo mode:', error);
      setErrorMessage('Could not update demo mode.');
    }
  };

  const handleFarmAliasBlur = async (farmId) => {
    setErrorMessage('');
    setSavingMessage('');
    try {
      await setFarmAlias(farmId, farmAliasDrafts[farmId] || '');
      setSavingMessage('Saved property alias.');
    } catch (error) {
      console.error('Failed to save farm alias:', error);
      setErrorMessage('Could not save property alias.');
    }
  };

  const handleParcelAliasBlur = async (parcelKey) => {
    if (!activeFarmId) return;
    setErrorMessage('');
    setSavingMessage('');
    try {
      await setParcelAlias(activeFarmId, parcelKey, parcelAliasDrafts[parcelKey] || '');
      setSavingMessage('Saved parcel alias.');
    } catch (error) {
      console.error('Failed to save parcel alias:', error);
      setErrorMessage('Could not save parcel alias.');
    }
  };

  // Phase 1: customer-facing demo controls. These write to the FARM doc and
  // are visible to every signed-in user (free, paid, admin). The per-admin
  // demo aliasing in the section above is independent and applies only to the
  // admin's own session.
  const handleTogglePublicDemo = async (farmId, enabled) => {
    setErrorMessage('');
    setSavingMessage('');
    try {
      await setFarmIsPublicDemo(farmId, enabled);
      setSavingMessage(enabled ? 'Property is now visible to all users as a demo.' : 'Property removed from public demo.');
    } catch (error) {
      console.error('Failed to toggle public demo flag:', error);
      setErrorMessage('Could not update public demo flag.');
    }
  };

  const handlePublicDemoAliasBlur = async (farmId) => {
    setErrorMessage('');
    setSavingMessage('');
    try {
      await setPublicDemoFarmAlias(farmId, publicDemoAliasDrafts[farmId] || '');
      setSavingMessage('Saved customer-facing demo alias.');
    } catch (error) {
      console.error('Failed to save customer-facing demo alias:', error);
      setErrorMessage('Could not save customer-facing demo alias.');
    }
  };

  const handlePublicDemoParcelAliasBlur = async (parcelKey) => {
    if (!activePublicDemoFarmId) return;
    setErrorMessage('');
    setSavingMessage('');
    try {
      await setPublicDemoParcelAlias(
        activePublicDemoFarmId,
        parcelKey,
        publicDemoParcelAliasDrafts[parcelKey] || ''
      );
      setSavingMessage('Saved customer-facing parcel alias.');
    } catch (error) {
      console.error('Failed to save customer-facing parcel alias:', error);
      setErrorMessage('Could not save customer-facing parcel alias.');
    }
  };

  if (loading) {
    return <div className="p-8 text-sm text-slate-500">Loading admin settings...</div>;
  }

  if (!isAdmin) {
    return <div className="p-8 text-sm text-slate-500">Admin access required.</div>;
  }

  return (
    <div className={`${embedded ? 'space-y-6' : 'p-8 max-w-6xl mx-auto space-y-6'}`}>
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Shield size={22} className="text-emerald-700" /> Demo Settings
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Demo mode anonymises property and parcel names in your session only.
            </p>
          </div>
          <button
            type="button"
            onClick={handleDemoToggle}
            className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-colors flex items-center gap-2 ${
              isDemoModeEnabled
                ? 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100'
                : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100'
            }`}
          >
            {isDemoModeEnabled ? <EyeOff size={16} /> : <Eye size={16} />}
            {isDemoModeEnabled ? 'Disable demo mode' : 'Enable demo mode'}
          </button>
        </div>
        {savingMessage && <p className="mt-3 text-xs text-emerald-700">{savingMessage}</p>}
        {errorMessage && <p className="mt-3 text-xs text-red-600">{errorMessage}</p>}
      </div>

      {/* Phase 1: Customer-facing demo properties. Separate concern from the
          per-admin demo mode above: this flag exposes a property to EVERY
          signed-in user (including free tier) as a shared demo. */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="flex items-start justify-between gap-4 mb-1">
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <Globe size={18} className="text-emerald-700" /> Customer-facing demo properties
          </h2>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          Properties marked here are visible to every signed-in user as a shared demo,
          including free-tier accounts who have no properties of their own.
          Set an optional customer-facing alias to anonymise the property name. This is independent
          of the per-admin demo mode above.
        </p>
        <div className="overflow-x-auto border border-slate-200 rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-3 py-2 font-semibold text-slate-600">Public Demo</th>
                <th className="text-left px-3 py-2 font-semibold text-slate-600">Real Name</th>
                <th className="text-left px-3 py-2 font-semibold text-slate-600">Customer-facing alias</th>
              </tr>
            </thead>
            <tbody>
              {farms.map((farm) => {
                const isPublic = publicDemoFarmIds.has(farm.id);
                return (
                  <tr key={farm.id} className="border-t border-slate-100">
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={isPublic}
                        onChange={(e) => handleTogglePublicDemo(farm.id, e.target.checked)}
                      />
                    </td>
                    <td className="px-3 py-2 text-slate-700">
                      {farm.farmName || farm.name || farm.id}
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={publicDemoAliasDrafts[farm.id] || ''}
                        onChange={(e) => setPublicDemoAliasDrafts((prev) => ({ ...prev, [farm.id]: e.target.value }))}
                        onBlur={() => handlePublicDemoAliasBlur(farm.id)}
                        placeholder="Leave blank to show real name"
                        className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-slate-400">
          Currently {publicDemoFarmIds.size} customer-facing demo {publicDemoFarmIds.size === 1 ? 'property' : 'properties'} selected.
        </p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <h2 className="text-lg font-bold text-slate-800 mb-1">Demo Properties (admin session)</h2>
        <p className="text-sm text-slate-500 mb-4">
          Select which properties are visible in your own per-admin demo mode. Aliases here apply only to your session.
        </p>
        <div className="overflow-x-auto border border-slate-200 rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-3 py-2 font-semibold text-slate-600">Use in Demo</th>
                <th className="text-left px-3 py-2 font-semibold text-slate-600">Real Name</th>
                <th className="text-left px-3 py-2 font-semibold text-slate-600">Demo Alias</th>
              </tr>
            </thead>
            <tbody>
              {farms.map((farm, index) => {
                const included = includedSet.has(farm.id);
                return (
                  <tr key={farm.id} className="border-t border-slate-100">
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={included}
                        onChange={(e) => handleToggleFarm(farm.id, e.target.checked)}
                      />
                    </td>
                    <td className="px-3 py-2 text-slate-700">{farm.farmName || farm.name || farm.id}</td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={farmAliasDrafts[farm.id] || ''}
                        onChange={(e) => setFarmAliasDrafts((prev) => ({ ...prev, [farm.id]: e.target.value }))}
                        onBlur={() => handleFarmAliasBlur(farm.id)}
                        placeholder={getFarmDisplayName(farm, index)}
                        className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <h2 className="text-lg font-bold text-slate-800 mb-1">Parcel Aliases</h2>
        <p className="text-sm text-slate-500 mb-4">
          Choose a demo property and override parcel aliases if needed.
        </p>
        <div className="flex gap-3 items-center mb-4">
          <select
            value={activeFarmId}
            onChange={(e) => setActiveFarmId(e.target.value)}
            className="border border-slate-300 rounded px-3 py-2 text-sm min-w-[320px]"
          >
            <option value="">Select demo property…</option>
            {demoFarms.map((farm) => (
              <option key={farm.id} value={farm.id}>
                {getFarmDisplayName(farm)}
              </option>
            ))}
          </select>
          <span className="text-xs text-slate-500 flex items-center gap-1">
            <Save size={13} /> Aliases save when the field loses focus
          </span>
        </div>

        {!activeFarmId ? (
          <p className="text-sm text-slate-500">Select a demo property to manage parcel aliases.</p>
        ) : parcels.length === 0 ? (
          <p className="text-sm text-slate-500">No parcels available for this property yet.</p>
        ) : (
          <div className="space-y-2">
            {parcels.map((parcel, index) => (
              <div key={parcel.docId} className="grid grid-cols-[1fr_1fr] gap-3 items-center border border-slate-200 rounded-lg px-3 py-2">
                <div className="text-sm text-slate-700 truncate">{parcel.fallback}</div>
                <input
                  type="text"
                  value={parcelAliasDrafts[parcel.key] || ''}
                  onChange={(e) => setParcelAliasDrafts((prev) => ({ ...prev, [parcel.key]: e.target.value }))}
                  onBlur={() => handleParcelAliasBlur(parcel.key)}
                  placeholder={getParcelDisplayName(activeFarmId, parcel.key, parcel.fallback, index)}
                  className="border border-slate-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Customer-facing public-demo parcel aliases. Independent of the
          per-admin section above: these aliases are stored on the FARM doc
          (`publicDemoParcelAliases`) and display for every signed-in user
          who can see the public-demo property. The dropdown is filtered to
          farms with `isPublicDemo: true` only, so admins can't accidentally
          set a public alias on a non-demo farm. */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <h2 className="text-lg font-bold text-slate-800 mb-1 flex items-center gap-2">
          <Globe size={18} className="text-emerald-700" /> Public demo parcel aliases
        </h2>
        <p className="text-sm text-slate-500 mb-4">
          Set per-parcel aliases that appear for every signed-in viewer on a
          customer-facing demo property. Useful to anonymise field names for
          public demos without changing the underlying data.
        </p>
        <div className="flex gap-3 items-center mb-4">
          <select
            value={activePublicDemoFarmId}
            onChange={(e) => setActivePublicDemoFarmId(e.target.value)}
            className="border border-slate-300 rounded px-3 py-2 text-sm min-w-[320px]"
          >
            <option value="">Select public demo property…</option>
            {(publicDemoFarms || []).slice().sort(sortByName).map((farm) => (
              <option key={farm.id} value={farm.id}>
                {farm.publicDemoFarmAlias || farm.farmName || farm.name || farm.id}
              </option>
            ))}
          </select>
          <span className="text-xs text-slate-500 flex items-center gap-1">
            <Save size={13} /> Aliases save when the field loses focus
          </span>
        </div>

        {(!publicDemoFarms || publicDemoFarms.length === 0) ? (
          <p className="text-sm text-slate-500">
            No public demo properties yet. Tick a property in the &ldquo;Customer-facing demo properties&rdquo;
            section above to enable it as a public demo, then return here to set parcel aliases.
          </p>
        ) : !activePublicDemoFarmId ? (
          <p className="text-sm text-slate-500">Select a public demo property to manage its parcel aliases.</p>
        ) : publicDemoParcels.length === 0 ? (
          <p className="text-sm text-slate-500">No parcels available for this property yet.</p>
        ) : (
          <div className="space-y-2">
            {publicDemoParcels.map((parcel) => (
              <div key={parcel.docId} className="grid grid-cols-[1fr_1fr] gap-3 items-center border border-slate-200 rounded-lg px-3 py-2">
                <div className="text-sm text-slate-700 truncate">{parcel.fallback}</div>
                <input
                  type="text"
                  value={publicDemoParcelAliasDrafts[parcel.key] || ''}
                  onChange={(e) => setPublicDemoParcelAliasDrafts((prev) => ({ ...prev, [parcel.key]: e.target.value }))}
                  onBlur={() => handlePublicDemoParcelAliasBlur(parcel.key)}
                  placeholder="Leave blank to show real name"
                  className="border border-slate-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
